import React, { useState } from 'react';
import { Card } from '../../Shared/ui/card';
import { Button } from '../../Shared/ui/button';
import apiClient from '../../Shared/services/api';
import type { BillingActions } from '../../Shared/types';

interface PricingPageProps {
  billingActions: BillingActions;
}

const PricingPage: React.FC<PricingPageProps> = ({ billingActions }) => {
  const [interval, setBillingInterval] = useState<'monthly' | 'annual'>('monthly');
  const [loadingTierId, setLoadingTierId] = useState<number | null>(null);

  if (!billingActions.can_upgrade) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <p className="text-gray-500">
          No upgrade options are currently available for your account.
        </p>
      </div>
    );
  }

  const visibleTiers = billingActions.upgrade_tiers;

  const handleUpgrade = async (tierId: number): Promise<void> => {
    setLoadingTierId(tierId);
    try {
      const { checkout_url } = await apiClient.createCheckoutSession(tierId, interval);
      window.location.href = checkout_url;
    } catch (err) {
      console.error('Failed to create checkout session:', err);
    } finally {
      setLoadingTierId(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold text-gray-900 text-center mb-8">Choose a Plan</h1>

      <div className="flex justify-center mb-8">
        <div className="flex rounded-lg border border-gray-200 p-1 bg-gray-50">
          <button
            type="button"
            aria-pressed={interval === 'monthly'}
            onClick={() => setBillingInterval('monthly')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              interval === 'monthly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            aria-pressed={interval === 'annual'}
            onClick={() => setBillingInterval('annual')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              interval === 'annual'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Annual
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {visibleTiers.map((tier) => {
          const price =
            interval === 'annual' && tier.price_annual !== null
              ? tier.price_annual
              : tier.price_monthly;
          const isLoading = loadingTierId === tier.id;

          return (
            <Card key={tier.id} className="p-6 flex flex-col">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{tier.display_name}</h2>
              <div className="mb-2">
                <span className="text-3xl font-bold text-gray-900">${price.toFixed(2)}</span>
                <span className="text-gray-500 ml-1">
                  /{interval === 'annual' && tier.price_annual !== null ? 'yr' : 'mo'}
                </span>
              </div>
              {interval === 'annual' && tier.annual_savings_label && (
                <p className="text-sm text-green-600 mb-4">{tier.annual_savings_label}</p>
              )}
              <div className="mt-auto pt-4">
                <Button
                  className="w-full"
                  onClick={() => void handleUpgrade(tier.id)}
                  disabled={isLoading}
                >
                  {isLoading ? 'Redirecting...' : 'Upgrade'}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default PricingPage;
